/**
 * Format seconds into a human-readable duration string (e.g. "1:23" or "1:05:30").
 */
export function formatDuration(seconds: number): string {
    const total = Math.round(seconds);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;

    const pad = (n: number) => String(n).padStart(2, '0');

    return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/**
 * Placeholder used as the video thumbnail URL's trailing segment when no filename is
 * available. URL path segment only — never render this to the viewer.
 */
export const VIDEO_THUMBNAIL_URL_TITLE_FALLBACK = 'video';

/**
 * Builds the trailing segment of a MediaEngine video thumbnail URL.
 *
 * MediaEngine rejects the request with a 400 when this segment is empty, which happens
 * whenever no filename reaches the client — most commonly because the gallery has its
 * "Show Filenames" setting turned off, so the photo payload omits the original name.
 * The segment sits outside the signed ACL (only the first four segments are covered by
 * the HMAC), so any non-empty value is accepted.
 *
 * The return value is for URL building only; it is never shown to the viewer, so the
 * fallback cannot leak a filename the gallery deliberately hides.
 */
export function buildVideoThumbnailUrlTitle(filename?: string | null): string {
    const title = (filename || '').replace(/\.[^/.]+$/, '').replace(/[^a-zA-Z0-9\-_]/g, '-');

    return title || VIDEO_THUMBNAIL_URL_TITLE_FALLBACK;
}

const FRACTION_DIGITS = 7;
const FRACTION_SCALE = 10 ** FRACTION_DIGITS;

const pad = (n: number) => String(n).padStart(2, '0');
const padFraction = (n: number) => String(n).padStart(FRACTION_DIGITS, '0');

export const secondsToHMS = (seconds: number): string => {
    const safe = Math.max(0, seconds);
    const totalSeconds = Math.floor(safe);
    const fraction = Math.floor((safe - totalSeconds) * FRACTION_SCALE);
    const hh = pad(Math.floor(totalSeconds / 3600));
    const mm = pad(Math.floor((totalSeconds % 3600) / 60));
    const ss = pad(totalSeconds % 60);
    return `${hh}:${mm}:${ss}.${padFraction(fraction)}`;
};
